<template>
  <div class="app-shell" :class="{ sideways: isSideways, embedded }" @pointerdown.capture="registerActivity">
    <div class="app-stage" :class="{ sideways: isSideways }" :style="stageStyle">
      <RouterView />
      <section v-if="showIdlePrompt" class="age-overlay age-screen" @click.stop>
        <div class="age-frame idle-prompt-frame">
          <div class="topbar">
            <div></div>
            <button class="icon-btn" aria-label="Retour à l'accueil" @click="confirmStillHere">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M3 11l9-8 9 8" />
                <path d="M5 10v10h5v-6h4v6h5V10" />
              </svg>
            </button>
          </div>
          <div class="content center-col">
            <div class="age-fx" aria-hidden="true">
              <img class="age-fx-ring-o" src="/img/v2/rond-o.png" alt="" />
              <img class="age-fx-ring-v" src="/img/v2/rond-v-2.png" alt="" />
              <img class="age-fx-ring-j" src="/img/v2/rond-j.png" alt="" />
            </div>
            <h1 class="tt">Êtes-vous toujours là&nbsp;?</h1>
            <p class="body">
              Retour automatique à l'accueil dans {{ idleSecondsLeft }} seconde{{ idleSecondsLeft > 1 ? 's' : '' }}.
            </p>
            <div class="age-buttons">
              <button class="btn btn-primary" @click="confirmStillHere">Oui</button>
            </div>
          </div>
        </div>
      </section>
      <!--      <div v-if="printerOffline" class="printer-notification" role="status" aria-live="polite">-->
      <!--        Imprimante non connectée.-->
      <!--      </div>-->
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { registerSW } from 'virtual:pwa-register'
import { useKiosk } from '@/features/kiosk/controller'
import { isEmbedded } from '@/features/kiosk/embed'

let fullscreenAttempted = false
const router = useRouter()
const route = useRoute()
// En mode embarqué (iframe dans l'app mobile), on désactive les comportements
// propres à la borne physique : plein écran forcé, verrou d'orientation, timer
// d'inactivité. L'app parente gère la navigation et l'affichage du ticket.
const embedded = isEmbedded()
const isSideways = !embedded && (window as Window & { __KIOSK_SIDEWAYS__?: boolean }).__KIOSK_SIDEWAYS__ ===
  true

// La maquette est dessinée pour une borne 1080×1920. On garde ce cadre et on le
// met à l'échelle uniformément pour remplir n'importe quel écran sans déformer.
// En mode embarqué (iframe téléphone), on réduit le canvas virtuel : les tailles px
// de la maquette deviennent plus grandes relativement à l'écran → texte plus lisible.
// Valeur ajustable : plus elle est petite, plus le rendu est gros (mais resserré).
const EMBED_CANVAS_W = 820
const KIOSK_W = embedded ? EMBED_CANVAS_W : 1080
const KIOSK_H = embedded ? Math.round((EMBED_CANVAS_W * 1920) / 1080) : 1920
const scale = ref(1)
const $kiosk = useKiosk()

import * as Sentry from "@sentry/vue";

const printerOffline = computed(() => $kiosk.printerConnected === false)
// const IDLE_TIMEOUT_MS = 3000_000
const IDLE_TIMEOUT_MS = 30_000
const IDLE_PROMPT_SECONDS = 10
const idleRoutes = new Set(['bet-type', 'favorite', 'stake', 'summary'])

const showIdlePrompt = ref(false)
const idleSecondsLeft = ref(IDLE_PROMPT_SECONDS)

let idleTimeout: ReturnType<typeof setTimeout> | null = null
let idleCountdownTimer: ReturnType<typeof setInterval> | null = null

const isIdleEnabled = computed(() => !embedded && idleRoutes.has(String(route.name ?? '')))

function clearIdleTimers() {
  if (idleTimeout) {
    clearTimeout(idleTimeout)
    idleTimeout = null
  }

  if (idleCountdownTimer) {
    clearInterval(idleCountdownTimer)
    idleCountdownTimer = null
  }
}

function hideIdlePrompt() {
  showIdlePrompt.value = false
  idleSecondsLeft.value = IDLE_PROMPT_SECONDS
}

function goHomeFromIdle() {
  clearIdleTimers()
  hideIdlePrompt()
  void router.replace({ name: 'home' })
}

function startIdleCountdown() {
  if (!isIdleEnabled.value) return

  clearIdleTimers()
  showIdlePrompt.value = true
  idleSecondsLeft.value = IDLE_PROMPT_SECONDS

  idleCountdownTimer = setInterval(() => {
    idleSecondsLeft.value -= 1

    if (idleSecondsLeft.value <= 0) {
      goHomeFromIdle()
    }
  }, 1_000)
}

function scheduleIdlePrompt() {
  clearIdleTimers()

  if (!isIdleEnabled.value) {
    hideIdlePrompt()
    return
  }

  idleTimeout = setTimeout(startIdleCountdown, IDLE_TIMEOUT_MS)
}

function registerActivity() {
  if (!isIdleEnabled.value) return

  if (showIdlePrompt.value) {
    hideIdlePrompt()
  }

  scheduleIdlePrompt()
}

function confirmStillHere() {
  hideIdlePrompt()
  scheduleIdlePrompt()
}

watch(
  () => route.name,
  () => {
    scheduleIdlePrompt()
  },
  { immediate: true },
)

function updateScale() {
  const w = window.innerWidth
  const h = window.innerHeight
  // En mode latéral, le cadre est pivoté de 90° : largeur et hauteur écran sont inversées.
  scale.value = isSideways
    ? Math.min(w / KIOSK_H, h / KIOSK_W)
    : Math.min(w / KIOSK_W, h / KIOSK_H)
}

const stageStyle = computed(() => ({
  width: `${KIOSK_W}px`,
  height: `${KIOSK_H}px`,
  transform: isSideways
    ? `translate(-50%, -50%) rotate(90deg) scale(${scale.value})`
    : `translate(-50%, -50%) scale(${scale.value})`,
}))

// Intervalle de vérification des mises à jour (la borne ne rechargeant jamais la page,
// il faut interroger le service worker régulièrement pour détecter une nouvelle version).
// En mode autoUpdate, une nouvelle version détectée est appliquée et la page rechargée
// automatiquement, sans aucun prompt.
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000

registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return
    setInterval(() => {
      if (registration.installing || !navigator.onLine) return
      void registration.update()
    }, UPDATE_CHECK_INTERVAL_MS)
  },
})

if ('serviceWorker' in navigator) {
  // Détecte quand le nouveau Service Worker prend le relais
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('Mise à jour détectée : rechargement automatique de la borne...');
    window.location.reload();
  });
}

async function requestKioskFullscreen() {
  if (embedded) return
  if (fullscreenAttempted) return
  fullscreenAttempted = true

  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
    }
  } catch {
    // Ignoré: le vrai verrouillage se fait au niveau Windows/Edge kiosk.
  }

  try {
    // `lock` n'est pas typé dans la lib DOM standard mais existe sur les bornes/Chromium.
    await (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.(
      'portrait',
    )
  } catch {
    // Ignoré si le navigateur refuse.
  }
}

onMounted(() => {
  document.body.classList.toggle('sideways-mode', isSideways)
  updateScale()
  window.addEventListener('resize', updateScale)
  window.addEventListener('orientationchange', updateScale)
  if (!embedded) {
    void requestKioskFullscreen()
    window.addEventListener('pointerdown', requestKioskFullscreen, { once: true })
  }
  if (!embedded) {
    $kiosk.startPrinterPolling()
  }
})

onBeforeUnmount(() => {
  document.body.classList.remove('sideways-mode')
  window.removeEventListener('resize', updateScale)
  window.removeEventListener('orientationchange', updateScale)
  clearIdleTimers()
  $kiosk.stopPrinterPolling()
})
</script>
